import { z } from 'zod'
import type { AITool } from './types'
import { fuzzyScore, normalizeText } from '../fuzzy-search'

const parametersSchema = z.object({
  query: z.string()
    .min(1)
    .describe('Exercise name to search for in user history'),
  limit: z.number()
    .min(1)
    .max(10)
    .describe('Maximum number of results to return (1-10, default 5)')
})

interface UserExercise {
  templateId: string
  name: string
  lastPerformed: Date
  firstPerformed: Date
  timesPerformed: number
  totalSets: number
  primaryMover: string
}

export const searchUserExercises: AITool = {
  name: 'searchUserExercises',
  description: 'Search exercises from user\'s workout history.',
  parameters: parametersSchema,
  
  execute: async (args, context) => {
    const { query, limit = 5 } = args
    const { workoutData, exerciseTemplates } = context
    
    if (!workoutData.workouts || workoutData.workouts.length === 0) {
      return {
        summary: 'No workout history available.',
        query,
        matches: [],
        totalUserExercises: 0
      }
    }
    
    // Build comprehensive exercise history
    const exerciseMap = new Map<string, UserExercise>()
    
    // Process all workouts to build exercise history
    workoutData.workouts.forEach(workout => {
      const workoutDate = new Date(workout.start_time)
      
      workout.exercises?.forEach(exercise => {
        const templateId = exercise.exercise_template_id
        const existing = exerciseMap.get(templateId)
        
        if (!existing) {
          exerciseMap.set(templateId, {
            templateId,
            name: exercise.title,
            lastPerformed: workoutDate,
            firstPerformed: workoutDate,
            timesPerformed: 1,
            totalSets: exercise.sets?.length || 0,
            primaryMover: exercise.primaryMover || 'Unknown'
          })
        } else {
          existing.timesPerformed++
          existing.totalSets += exercise.sets?.length || 0
          if (workoutDate > existing.lastPerformed) {
            existing.lastPerformed = workoutDate
          }
          if (workoutDate < existing.firstPerformed) {
            existing.firstPerformed = workoutDate
          }
        }
      })
    })
    
    // Convert to array for searching
    const userExercises = Array.from(exerciseMap.values())
    
    // Normalize and expand query
    const normalizedQuery = normalizeText(query)
    
    // Score user's exercises
    const scoredExercises = userExercises.map(exercise => {
      // Calculate text match score
      const textScore = fuzzyScore(normalizedQuery, exercise.name)
      
      // Boost for exact word matches
      const queryWords = normalizedQuery.split(' ')
      const nameWords = normalizeText(exercise.name).split(' ')
      let wordBonus = 0
      queryWords.forEach(word => {
        if (nameWords.includes(word)) {
          wordBonus += 15
        }
      })
      
      // Calculate recency score (0-30 points based on last 90 days)
      const daysSinceLastPerformed = Math.floor(
        (new Date().getTime() - exercise.lastPerformed.getTime()) / (1000 * 60 * 60 * 24)
      )
      const recencyScore = Math.max(0, 30 - (daysSinceLastPerformed / 3))
      
      // Calculate frequency score (0-20 points)
      const frequencyScore = Math.min(20, exercise.timesPerformed * 2)
      
      // Combined score
      const finalScore = textScore + wordBonus + recencyScore + frequencyScore
      
      return {
        ...exercise,
        score: finalScore,
        daysSinceLastPerformed
      }
    })
    
    // Filter and sort matches
    const matches = scoredExercises
      .filter(match => match.score > 20) // Higher threshold for user exercises
      .sort((a, b) => b.score - a.score)
      .slice(0, limit)
    
    return {
      query,
      matchCount: matches.length,
      matches: matches.map(m => ({
        templateId: m.templateId,
        name: m.name,
        primaryMover: m.primaryMover,
        lastPerformed: m.lastPerformed.toISOString().split('T')[0],
        daysSinceLastPerformed: m.daysSinceLastPerformed,
        timesPerformed: m.timesPerformed,
        totalSets: m.totalSets,
        averageSetsPerSession: Math.round(m.totalSets / m.timesPerformed * 10) / 10
      })),
      totalUserExercises: userExercises.length
    }
  }
}